import { useRef, useState } from 'react';
import { B } from '../../constants/brand';

// Wraps an area so files can be dropped onto it. `onFiles` receives an array of
// File objects (folders are ignored — dataTransfer.files carries files only).
// `accept` (e.g. ['.xlsx']) drops anything with another extension and reports it
// through `onRejected`; `multiple={false}` keeps only the first file.
export function filterDropped(files, { accept, multiple = true } = {}) {
  const list = Array.from(files || []);
  const exts = (accept || []).map(e => e.toLowerCase());
  const ok = [];
  const rejected = [];
  for (const f of list) {
    const name = String(f?.name || '').toLowerCase();
    if (exts.length && !exts.some(e => name.endsWith(e))) rejected.push(f);
    else ok.push(f);
  }
  return { files: multiple ? ok : ok.slice(0, 1), rejected };
}

export default function FileDropZone({ onFiles, onRejected, disabled, accept, multiple = true, label = 'Drop files to upload', children, style }) {
  const [over, setOver] = useState(false);
  const depth = useRef(0);
  const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');

  const enter = (e) => {
    if (disabled || !hasFiles(e)) return;
    e.preventDefault();
    depth.current += 1;
    setOver(true);
  };
  const leave = (e) => {
    if (disabled || !hasFiles(e)) return;
    depth.current = Math.max(0, depth.current - 1);
    if (!depth.current) setOver(false);
  };
  const dragOver = (e) => {
    if (disabled || !hasFiles(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };
  const drop = (e) => {
    if (disabled || !hasFiles(e)) return;
    e.preventDefault();
    e.stopPropagation();
    depth.current = 0;
    setOver(false);
    const { files, rejected } = filterDropped(e.dataTransfer.files, { accept, multiple });
    if (rejected.length && onRejected) onRejected(rejected);
    if (files.length) onFiles(files);
  };

  return (
    <div onDragEnter={enter} onDragLeave={leave} onDragOver={dragOver} onDrop={drop}
      style={{ position: 'relative', ...style }}>
      {children}
      {over && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 5, borderRadius: 10,
          border: `2px dashed ${B.teal}`, background: 'rgba(43,122,140,0.08)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none',
        }}>
          <div style={{ background: B.surface, color: B.navy, fontWeight: 600, fontSize: 13, padding: '8px 14px', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>
            {label}
          </div>
        </div>
      )}
    </div>
  );
}
